import { IconMoreHorizontal, IconPlus } from '@developerz/ui';
import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type SeatInfo, UNLIMITED_AI_DEVS } from '../lib/billing';
import { orgHref } from '../lib/org';
import { OrgUnfundedAlert } from './OrgUnfundedAlert';
import { PageHeader } from './PageHeader';
import { Popover } from './Popover';
import { Button, buttonClass } from './ui/Button';

// The Fleet page's header (#6559, prototype `page()` header): the title, the live
// eyebrow, "..." (update the software on every machine; AI roles and models, which
// brings the page's own section into view, #6788), Plans,
// the one brand button ("Connect a machine", `outline` on an empty fleet, where the
// first-run screen holds the brand button, #6567) and the plan line under them.
// Presenter: the container passes the entitlements read and the role verdict.
//
// THE PLAN LINE is the comparison every connect makes: machines holding a slot
// (`active_boxes`, the server's count, which includes setup codes minted and not yet
// used) against the plan's limit (`enrollment_box_budget`). Read-only, so every role
// sees it (`read:billing` is held by all of them). An ORG at a limit of 0 gets the
// named refusal under the header INSTEAD of the line, before anyone clicks Connect.
//
// WRITE ACTIONS ARE ADMIN-ONLY (owner ruling G21): a member or viewer sees no
// "Connect a machine" and no "Update software" item. The server would refuse both
// (`write:runners`); hiding them is the UX half.

export type FleetHeaderProps = {
  /** The entitlements read, or `undefined` while it is in flight or failed. */
  readonly seat: SeatInfo | undefined;
  /** The entitlements read failed with nothing cached: the plan line says so (#298). */
  readonly seatError: boolean;
  /** The account is an org with a limit of 0 (`isOrgUnfunded`, `@developerz/domain`). */
  readonly orgUnfunded: boolean;
  /** `canAdminWrite` on a settled role: the caller may connect and update machines. */
  readonly canWrite: boolean;
  /**
   * The fleet has no machine: the first-run screen's button is the view's one
   * brand button (#6567), so Connect here renders `outline`.
   */
  readonly firstRun?: boolean | undefined;
  readonly onConnect: () => void;
  readonly onUpdateSoftware: () => void;
  /** "AI roles and models": scroll to the page's own section and focus its first role (#6788). */
  readonly onShowRoles: () => void;
};

/**
 * Machines against the limit. Over it is its own sentence: seats can lapse under
 * a fleet (machines are never evicted), and "5 of 3" reads as a typo, not as an
 * account that must remove machines or raise the limit before connecting another.
 */
function planLine(seat: SeatInfo): string {
  const active = seat.active_boxes;
  const budget = seat.entitlements.enrollment_box_budget;
  if (budget === UNLIMITED_AI_DEVS) return t('fleet.top.plan_unlimited', { active });
  return active > budget
    ? t('fleet.top.plan_over', { active, budget })
    : t('fleet.top.plan_line', { active, budget });
}

export function FleetHeader(props: FleetHeaderProps): JSX.Element {
  return (
    <>
      <PageHeader
        title={t('fleet.title')}
        eyebrow={t('app.live')}
        desc={t('fleet.top.subtitle')}
        actions={
          <div class="fleet-head__actions">
            <div class="fleet-head__buttons">
              <Popover
                label={t('fleet.top.more_actions')}
                trigger={<IconMoreHorizontal />}
                triggerClass={buttonClass({ variant: 'outline', size: 'icon' })}
                panelClass="fleet-head__menu"
              >
                {(close) => (
                  <ul>
                    <Show when={props.canWrite}>
                      <li>
                        <button
                          type="button"
                          class="popover__item"
                          onClick={() => {
                            close();
                            props.onUpdateSoftware();
                          }}
                        >
                          {t('fleet.top.update_software')}
                        </button>
                      </li>
                    </Show>
                    <li>
                      <button
                        type="button"
                        class="popover__item"
                        onClick={() => {
                          close();
                          props.onShowRoles();
                        }}
                      >
                        {t('fleet.top.ai_roles')}
                      </button>
                    </li>
                  </ul>
                )}
              </Popover>
              {/* The epics index (`/fleet/plans`): it nests here rather than taking a
                  rail row, so this is the one way in. Not billing plans. */}
              <A
                class={buttonClass({ variant: 'outline', size: 'sm' })}
                href={orgHref('/fleet/plans')}
              >
                {t('fleet.plans.link')}
              </A>
              <Show when={props.canWrite}>
                <Button
                  variant={props.firstRun === true ? 'outline' : 'brand'}
                  size="sm"
                  onClick={() => props.onConnect()}
                >
                  <IconPlus />
                  {t('fleet.top.connect')}
                </Button>
              </Show>
            </div>
            <Show
              when={props.seat}
              fallback={
                <Show when={props.seatError}>
                  <p class="fleet-head__plan">{t('fleet.stat_unavailable')}</p>
                </Show>
              }
            >
              {/* An org at a limit of 0: the refusal under the header speaks, as in
                  `ConnectPlanLine`; "3 of 0 machines" above it said nothing more. */}
              {(seat) => (
                <Show when={!props.orgUnfunded}>
                  <p class="fleet-head__plan">{planLine(seat())}</p>
                </Show>
              )}
            </Show>
          </div>
        }
      />
      <Show when={props.orgUnfunded}>
        <OrgUnfundedAlert />
      </Show>
    </>
  );
}
